import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

// Construit les liens en fonction de la fonction de traduction t()
const getNavLinks = (t) => [
  { path: '/', label: t('nav.home') },
  { path: '/la-maison', label: t('nav.house') },
  {
    label: t('nav.collections'),
    children: [
      { path: '/collection', label: t('nav.reverie') },
    ],
  },
  { path: '/notre-vision', label: t('nav.fabrication') },
  { path: '/contact', label: t('nav.contact') },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [solidBg, setSolidBg] = useState(false);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const location = useLocation();
  const { t } = useLang();
  const navLinks = getNavLinks(t);
  const isHome = location.pathname === '/';
  const isProduct = location.pathname.startsWith('/collection/');
  const isCollection = location.pathname === '/collection';

  useEffect(() => {
    const handleScroll = () => {
      if (isProduct) {
        setSolidBg(false);
        return;
      }
      if (isHome) {
        const threshold = 3.5 * window.innerHeight - 100;
        setSolidBg(window.scrollY > threshold);
        return;
      }
      if (isCollection) {
        const threshold = 0.55 * window.innerHeight;
        setSolidBg(window.scrollY > threshold);
        return;
      }
      setSolidBg(true);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome, isProduct, isCollection]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const isTransparent = isOpen || (isHome && !solidBg) || isProduct || (isCollection && !solidBg);

  const navBg = isTransparent
    ? 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md border-b border-gray-100';

  const linkColor = isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900';
  const activeColor = isTransparent ? 'text-white' : 'text-gray-900';

  // Un parent dropdown est "actif" si la route courante matche un de ses enfants
  const isParentActive = (children) => children.some(c => c.path === location.pathname);

  const closeMobile = () => {
    setIsOpen(false);
    setOpenMobileDropdown(null);
  };

  return (
    <>
    <nav className={`fixed top-0 w-full z-[60] transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="transition-opacity duration-300 hover:opacity-70">
          <img
            src="/photos/Logo_fd_transparent.png"
            alt="Pernety"
            className="h-20 w-auto"
            style={{ filter: isTransparent || isOpen ? 'invert(1) brightness(2)' : 'none' }}
          />
        </Link>

        {/* ── DESKTOP NAV ── */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => {
            // Lien simple
            if (!link.children) {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                    active ? activeColor : linkColor
                  }`}
                >
                  {link.label}
                </Link>
              );
            }

            // Lien avec dropdown
            const active = isParentActive(link.children);
            const open = openDesktopDropdown === link.label;
            return (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setOpenDesktopDropdown(link.label)}
                onMouseLeave={() => setOpenDesktopDropdown(null)}
              >
                <button
                  type="button"
                  className={`inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 cursor-default ${
                    active ? activeColor : linkColor
                  }`}
                >
                  {link.label}
                  <svg
                    className={`w-2.5 h-2.5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                    viewBox="0 0 12 8" fill="none">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Dropdown panel */}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 transition-all duration-300
                  ${open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                  <div className="min-w-[180px] bg-white shadow-xl border border-gray-100 py-2">
                    {link.children.map(child => {
                      const childActive = location.pathname === child.path;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`block px-5 py-3 text-[12px] tracking-[0.15em] uppercase transition-colors text-center font-light
                            ${childActive ? 'text-gray-900 bg-gray-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Switcher de langue */}
          <div className="pl-4 ml-2 border-l border-current/20">
            <LanguageSwitcher variant="desktop" colorScheme={isTransparent ? 'light' : 'dark'} />
          </div>
        </div>

        {/* Mobile burger */}
        <button
          className="md:hidden relative w-7 h-6 z-50 flex flex-col justify-between"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <span className={`block h-px w-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] origin-center
            ${isOpen
              ? 'rotate-45 translate-y-[11px] bg-white'
              : (isTransparent ? 'bg-white' : 'bg-gray-800')}`} />
          <span className={`block h-px transition-all duration-300 ease-out
            ${isOpen
              ? 'opacity-0 w-0'
              : `opacity-100 ${isTransparent ? 'bg-white w-3/4' : 'bg-gray-800 w-3/4'}`}`} />
          <span className={`block h-px w-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] origin-center
            ${isOpen
              ? '-rotate-45 -translate-y-[12px] bg-white'
              : (isTransparent ? 'bg-white' : 'bg-gray-800')}`} />
        </button>
      </div>
    </nav>

      {/* ── MOBILE MENU FULLSCREEN ── */}
      <div
        className={`md:hidden fixed inset-0 z-[55] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{
          backgroundColor: '#0a0405',
          backgroundImage: 'linear-gradient(180deg, #1a0a0d 0%, #0a0405 100%)',
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <p className="font-serif text-[20rem] tracking-normal text-white/[0.02] leading-none select-none">P</p>
        </div>

        <div className="relative h-full flex flex-col overflow-y-auto">
          <div className="h-[88px] shrink-0" />

          <div className="flex-1 flex flex-col items-center justify-center px-8 py-6">
            <div className="w-full max-w-sm flex flex-col items-center gap-5">

              <div className={`flex items-center justify-center gap-3 mb-2 transition-all duration-700 delay-100
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                <div className="w-10 h-px bg-white/30" />
                <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
                <div className="w-10 h-px bg-white/30" />
              </div>

              {/* Navigation mobile */}
              {navLinks.map((link, i) => {
                const baseTransition = `transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
                  ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`;
                const baseStyle = { transitionDelay: isOpen ? `${150 + i * 80}ms` : '0ms' };

                // Lien simple
                if (!link.children) {
                  const active = location.pathname === link.path;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobile}
                      className={`group relative ${baseTransition}`}
                      style={baseStyle}
                    >
                      <span className={`block font-normal text-lg tracking-wide transition-colors duration-300
                        ${active ? 'text-white' : 'text-white/65'}`}
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        {link.label}
                      </span>
                      <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 h-px bg-white/40 transition-all duration-500
                        ${active ? 'w-12 opacity-100' : 'w-0 opacity-0'}`} />
                    </Link>
                  );
                }

                // Lien avec dropdown (accordéon mobile)
                const active = isParentActive(link.children);
                const expanded = openMobileDropdown === link.label;
                return (
                  <div
                    key={link.label}
                    className={`w-full flex flex-col items-center ${baseTransition}`}
                    style={baseStyle}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenMobileDropdown(expanded ? null : link.label)}
                      className="group relative flex items-center gap-2"
                    >
                      <span className={`block font-normal text-lg tracking-wide transition-colors duration-300
                        ${active ? 'text-white' : 'text-white/65'}`}
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        {link.label}
                      </span>
                      <svg
                        className={`w-3 h-3 text-white/50 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                        viewBox="0 0 12 8" fill="none">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>

                    {/* Children expansés */}
                    <div className={`overflow-hidden transition-all duration-500 ease-out w-full
                      ${expanded ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
                      <div className="flex flex-col items-center gap-3">
                        {link.children.map(child => {
                          const childActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={closeMobile}
                              className="relative"
                            >
                              <span className={`block font-light italic text-xl tracking-wide transition-colors duration-300
                                ${childActive ? 'text-white' : 'text-white/45 hover:text-white/80'}`}
                                style={{ fontFamily: "'Playfair Display', serif" }}>
                                {child.label}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className={`flex items-center justify-center gap-3 mt-2 transition-all duration-700
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: isOpen ? `${150 + navLinks.length * 80}ms` : '0ms' }}>
                <div className="w-10 h-px bg-white/30" />
                <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
                <div className="w-10 h-px bg-white/30" />
              </div>
            </div>
          </div>

          <div className={`shrink-0 px-8 pb-12 pt-6 transition-all duration-700
            ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: isOpen ? `${150 + (navLinks.length + 1) * 80}ms` : '0ms' }}>
            <div className="flex flex-col items-center gap-4">
              {/* Switcher de langue mobile */}
              <div className="mb-2">
                <LanguageSwitcher variant="mobile" colorScheme="light" />
              </div>
              <a
                href="https://www.instagram.com/pernety_jewelry/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 text-xs tracking-[0.25em] uppercase text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @pernety_jewelry
              </a>
              <a
                href="mailto:contact@pernetyjewelry.com"
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                contact@pernetyjewelry.com
              </a>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/20 mt-2">
                &copy; {new Date().getFullYear()} Pernety
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
